import React, { Fragment, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { startEmptyCartOrder } from "../features/order/orderActions";
import {
  getCustomerOffersRequest,
  reconcileItemOffer,
  updateCartItem,
  toggleCartView,
} from "../features/cart/cartActions";
import {
  appliedCoupon,
  clearAppliedCoupon,
} from "../features/restaurant/restaurantAction";
import {
  isExclusiveCoupon,
  isOfferDismissed,
  isAllAutoApplySuppressed,
  clearAllAutoApplySuppression,
  bogoEqualOrLesserSatisfied,
  computeBogoItemClaims,
  computeRawCartSubtotal,
  isMenuItemOrderable,
  getOfferCategory,
} from "../utils/offer-utils";
import { getCookie } from "../../helper/cookieHelper";
import { USER_ID } from "../constants";
import { useParams } from "react-router";
import { showErrorToast, showErrorToastDesktop } from "../utils/toastUtils";

// Effective unit price for a cart entry — base item price plus the per-unit
// sum of modifier/customization prices. Matches how INCREMENT_CART_ITEM
// computes subTotal in the cart reducer.
const computeEffectiveUnitPrice = (item: any): number => {
  const base = Number(item?.price || 0);
  const modifiers = Array.isArray(item?.options)
    ? item.options.reduce(
        (sum: number, opt: any) =>
          sum +
          (Number(opt?.price) || 0) *
            (Number(opt?.quantity) > 0 ? Number(opt?.quantity) : 1),
        0
      )
    : 0;
  return base + modifiers;
};

export const CartWatcherService = () => {
  const dispatch = useDispatch();
  const cartItemCount = useSelector(
    (state: any) => state.cart.cart.items.length
  );
  const cartInit = useSelector((state: any) => state.cart.cartInitialized);
    let { tableId } = useParams<any>();

  // Open the mini-cart (SpinnerFooter) when an offer is removed on menu pages
  // so the user can immediately see the updated totals. On the cart/checkout
  // pages the cart is already visible, so the toggle is skipped there.
  const openCartOnMenuPage = () => {
    if (window.location.pathname.includes('/menu/')) {
      dispatch(toggleCartView(true));
    }
  };

  // Empty-cart housekeeping (existing behavior).
  useEffect(() => {
    if (
      cartItemCount == 0 &&
      cartInit &&
      !window.location.pathname.includes("table")
    ) {
      dispatch(startEmptyCartOrder());
    }
  }, [cartItemCount]);

  // Treat every fresh page load as a clean slate for offer dismissals. A
  // reload should re-evaluate ALL auto-apply offers from scratch — it must not
  // carry over an offer the user trash-removed in a previous page view.
  // Dismissals live in sessionStorage (which survives a reload), so wipe the
  // auto-apply dismissal keys once on mount. This service mounts once per page
  // load (it's high in the App tree), so the effect runs exactly once per
  // refresh. (Logout has its own equivalent wipe further below.)
  useEffect(() => {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith("autoApplyDismissedOffer:")) toRemove.push(k);
      }
      toRemove.forEach((k) => sessionStorage.removeItem(k));
      // The global "user removed an auto offer → stop auto-applying" flag is
      // likewise a per-session signal: a reload starts fresh and re-evaluates
      // all auto-apply offers.
      clearAllAutoApplySuppression();
    } catch {
      /* sessionStorage unavailable */
    }
  }, []);

  // --- BOGO detection / apply / restore --------------------------------------
  // Lives here (globally mounted) so it runs regardless of which page the user
  // is on — including the cart page where `INCREMENT_CART_ITEM` overwrites
  // `subTotal = qty × price` and would otherwise blow away the discount.
  //
  // For every BOGO offer in `customerOffers`:
  //   * auto_apply → drive by cart contents alone
  //   * non-auto_apply → only act once the user has clicked Apply (offer is
  //     in `appliedCoupons`)
  //
  // Eligibility: buy side must be fully met (`buyQty ≥ bogo.buyQty`), but the
  // get side activates as soon as the cart has at least one get unit — the
  // discount then scales linearly with the get qty present, capped at
  // `bogo.getQty`. Same-item BOGO uses the same rule: `total ≥ buyQty + 1`.
  // The discount target is the highest-priced eligible get entry. Only the
  // first `min(qtyAvailable, getQty)` units are discounted; the remainder
  // stay at full unit price. The effect is idempotent: it recomputes the
  // desired subTotal each tick and only dispatches when the cart drifts.
  const cartItems = useSelector((state: any) => state.cart.cart.items || []);
  const cartItemsFromApi = useSelector((state: any) => state.cart.cartItems);
  const customerOffers = useSelector(
    (state: any) => state.cart.customerOffers || []
  );
  const appliedCoupons = useSelector(
    (state: any) => state.restaurant.appliedCoupons || []
  );
  const restaurantOrderTypeId = useSelector(
    (state: any) => state.restaurant.restaurantOrderTypeId
  );
  const restaurantMenuDetails = useSelector(
    (state: any) => state.restaurant.restaurantMenuDetails || []
  );
  const isMobile = useSelector((state: any) => state?.navigation?.isMobile);

  const disabledItemIds: string[] = React.useMemo(() => {
    if (!Array.isArray(cartItemsFromApi) || !Array.isArray(cartItems)) return [];
    const ids: string[] = [];
    cartItems.forEach((item: any) => {
      const apiItem = cartItemsFromApi.find((i: any) => i?.itemId === item?.itemId);
      if (apiItem && apiItem.enabled === false) ids.push(item.itemId);
    });
    return ids;
  }, [cartItemsFromApi, cartItems]);

  // CD (dine-in table) and Buzzer orders never participate in offers.
  // Deliberately keyed ONLY on [tableId] (not cartItems/appliedCoupons) — it
  // fires once on mount and again if/when the flow flag itself resolves, but
  // never re-fires from its own or another effect's cart dispatch. That keeps
  // it a one-shot cleanup instead of a continuously reactive effect, which is
  // what previously caused an infinite dispatch loop against the FREE_ITEM
  // chosen-item reconciliation effect below (both fighting over
  // `appliedCoupons` on every cart mutation).
  useEffect(() => {
    const inOffersHiddenFlow =
      window.location.pathname.includes('/table/') || tableId;
    if (!inOffersHiddenFlow) return;

    if (Array.isArray(appliedCoupons) && appliedCoupons.length > 0) {
      dispatch(clearAppliedCoupon());
    }

    if (!Array.isArray(cartItems)) return;
    cartItems.forEach((item: any) => {
      const isBogoDiscount =
        item?.is_discount_applied === true && item?.offer_data?.isBogoFreeItem === true;
      const isItemLevelDiscount = item?.offerId != null;
      if (!isBogoDiscount && !isItemLevelDiscount) return;

      if (isBogoDiscount) {
        const unitPrice = computeEffectiveUnitPrice(item);
        const qty = Number(item?.quantity || 0);
        dispatch(updateCartItem(item.itemId, String(unitPrice * qty), false, qty, qty, undefined, false, -1));
        return;
      }

      const menuItem = Array.isArray(restaurantMenuDetails)
        ? restaurantMenuDetails.find((m: any) => m?.itemId === item?.itemId)
        : null;
      const regularPrice = Number(menuItem?.price ?? item?.originalPrice ?? item?.price ?? 0);
      dispatch(
        reconcileItemOffer({
          itemId: item.itemId,
          modifierIds: item.modifierIds,
          apply: false,
          price: regularPrice,
          offerId: null,
          originalPrice: regularPrice,
        })
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  // Drop applied coupons that aren't valid for the current order type. When the
  // user switches order type (e.g. delivery → pickup), any applied offer whose
  // `orderTypeIds` is restricted and doesn't include the new type no longer
  // qualifies and is removed. Empty/missing `orderTypeIds` = "all types", kept.
  // Covers manual AND auto coupons, on every page (mobileCart's auto-apply only
  // re-evaluates auto coupons and only while it's mounted). BOGO offers are
  // additionally gated inside the BOGO pass below so they don't auto re-apply.
  useEffect(() => {
    if (!restaurantOrderTypeId) return;
    if (!Array.isArray(appliedCoupons) || appliedCoupons.length === 0) return;
    appliedCoupons.forEach((c: any) => {
      const typeIds = c?.orderTypeIds;
      const restricted = Array.isArray(typeIds) && typeIds.length > 0;
      if (restricted && !typeIds.includes(restaurantOrderTypeId) && c?.id != null) {
        dispatch(clearAppliedCoupon({ id: c.id }));
        // const msg = `Offer '${c?.name ?? 'Offer'}' removed — order no longer qualifies`;
         const msg = `Some offers are no longer available. Your order has been updated.`;
        isMobile ? showErrorToast(msg, `offer-removed-${c.id}`, 2500) : showErrorToastDesktop(msg, `offer-removed-${c.id}`, 2500);
        openCartOnMenuPage();
      }
    });
  }, [restaurantOrderTypeId, appliedCoupons, dispatch]);

  // Cart emptied → drop every applied coupon. The min_subtotal sweep below
  // can't cover this: it skips BOGO entirely and only fires when
  // `min_subtotal > 0`, so a $0-minimum or BOGO offer would survive an empty
  // cart and silently reattach itself to the next order the user starts.
  // `appliedCoupons` lives in the restaurant reducer, which listens to no
  // cart-reset action, so nothing else clears it either.
  //
  // Keyed on the non-empty → empty TRANSITION (not "is empty") so a not-yet-
  // hydrated cart on first mount can't wipe coupons before items arrive.
  // Offer-granted free items don't count as cart contents — a cart holding
  // only those is still empty as far as the customer is concerned.
  const cartHadItemsRef = useRef(false);
  useEffect(() => {
    if (!Array.isArray(cartItems)) return;
    const hasPurchasedItems = cartItems.some(
      (item: any) => Number(item?.quantity || 0) > 0 && !item?.offer_data?.isFreeItem
    );
    if (hasPurchasedItems) {
      cartHadItemsRef.current = true;
      return;
    }
    if (!cartHadItemsRef.current) return;
    cartHadItemsRef.current = false;
    if (Array.isArray(appliedCoupons) && appliedCoupons.length > 0) {
      dispatch(clearAppliedCoupon());
    }
  }, [cartItems, appliedCoupons, dispatch]);

  // Remove applied coupons whose min_subtotal is no longer met (e.g. user
  // decreases quantity). BOGO offers handle their own eligibility above.
  // Excludes unavailable items (disabled for current order type) from the
  // subtotal so their price doesn't count toward offer eligibility.
  useEffect(() => {
    if (!Array.isArray(appliedCoupons) || appliedCoupons.length === 0) return;
    if (!Array.isArray(cartItems)) return;
    const availableItems = cartItems.filter(
      (item: any) => !disabledItemIds.includes(item?.itemId)
    );
    const subTotal = computeRawCartSubtotal(availableItems);
    appliedCoupons.forEach((c: any) => {
      const offerType = String(c?.offer_type ?? c?.offerType ?? '').toUpperCase();
      if (offerType === 'BOGO') return;
      const minSub = Number(c?.min_subtotal ?? 0);
      if (minSub > 0 && subTotal < minSub) {
        dispatch(clearAppliedCoupon({ id: c.id }));
        const msg = `Some offers are no longer available. Your order has been updated.`;
        isMobile
          ? showErrorToast(msg, `offer-removed-${c.id}`, 3500)
          : showErrorToastDesktop(msg, `offer-removed-${c.id}`, 3500);
        openCartOnMenuPage();
      }
    });
  }, [cartItems, appliedCoupons, dispatch, disabledItemIds]);

  // When the order type changes or the menu updates, correct the chosen free
  // item for any applied FREE_ITEM offer whose selected item is no longer
  // available (not in menu or enable===false). If no items remain available,
  // remove the offer entirely so the cart doesn't silently carry a no-op coupon.
  useEffect(() => {
    if (window.location.pathname.includes('/table/') || tableId) return;
    if (!Array.isArray(appliedCoupons) || appliedCoupons.length === 0) return;
    if (!Array.isArray(restaurantMenuDetails) || restaurantMenuDetails.length === 0) return;
    appliedCoupons.forEach((c: any) => {
      const t = String(c?.offer_type ?? c?.offerType ?? '').toUpperCase();
      if (t !== 'FREE_ITEM') return;
      const freeList: any[] = Array.isArray(c?.freeList) ? c.freeList : [];
      const availableItems = freeList
        .map((id: any) => restaurantMenuDetails.find((m: any) => m?.itemId === id))
        .filter(isMenuItemOrderable);
      if (availableItems.length === 0) {
        dispatch(clearAppliedCoupon({ id: c.id }));
        // const msg = `Offer '${c?.name ?? 'Offer'}' removed — order no longer qualifies`;
         const msg = `Some offers are no longer available. Your order has been updated.`;
        isMobile ? showErrorToast(msg, `offer-removed-${c.id}`, 3500) : showErrorToastDesktop(msg, `offer-removed-${c.id}`, 3500);
        openCartOnMenuPage();
        return;
      }
      const chosenStillAvailable = availableItems.some(
        (m: any) => m?.itemId === c._chosenFreeItemId
      );
      if (!chosenStillAvailable) {
        dispatch(appliedCoupon({ ...c, _chosenFreeItemId: availableItems[0].itemId }));
      }
    });
  }, [restaurantOrderTypeId, restaurantMenuDetails, appliedCoupons, dispatch]);

  useEffect(() => {
    if (!Array.isArray(customerOffers) || customerOffers.length === 0) return;
    if (!Array.isArray(cartItems)) return;
    if (window.location.pathname.includes('/table/') || tableId) return;

    const bogoOffers = customerOffers.filter((o: any) => {
      const t = String(o?.offer_type ?? o?.offerType ?? "").toUpperCase();
      return t === "BOGO" && o?.bogo;
    });
    if (bogoOffers.length === 0) return;

    // Raw subtotal (qty × base+modifiers, no discount applied) — stable across
    // BOGO toggles so a Remove click can't trip its own dismissal release.
    const subTotalNow = computeRawCartSubtotal(cartItems);

    // Global suppression: once the user removed an auto-applied offer, no
    // offer may auto-apply (BOGO included) until they manually apply one.
    // Already-applied BOGOs (in `appliedCoupons`) are kept; only NEW auto
    // applies are blocked. Item-level offers are reconciled elsewhere and are
    // unaffected.
    const autoApplySuppressed = isAllAutoApplySuppressed();

    // Shared buy-side claim ledger for this BOGO pass. Each unit of a buy
    // item in the cart can only be consumed by ONE BOGO offer — so if
    // meen kulambu (qty 1) is the buy item for both "meen → idly" and
    // "meen → dosa", only the first BOGO in the list claims it; the second
    // sees zero available buy qty and goes ineligible. This also matches
    // user intent: 1 buy never unlocks 2 free items via parallel offers.
    //
    // Keyed by itemId → claimed qty.
    const claimedBuyQty = new Map<string, number>();

    bogoOffers.forEach((offer: any) => {
      const bogo = offer?.bogo || {};
      const offerId = offer?.id;
      const buyItemIds: string[] = (Array.isArray(bogo?.buyItems)
        ? bogo.buyItems
        : []
      )
        .map((b: any) => b?.itemId)
        .filter(Boolean);
      const getItemIds: string[] = (Array.isArray(bogo?.getItems)
        ? bogo.getItems
        : []
      )
        .map((g: any) => g?.itemId)
        .filter(Boolean);
      const buyQty = Number(bogo?.buyQty || 1);
      const getQty = Number(bogo?.getQty || 1);
      const autoApply = offer?.auto_apply === true;
      if (
        !offerId ||
        buyItemIds.length === 0 ||
        getItemIds.length === 0 ||
        !buyQty ||
        !getQty
      ) {
        return;
      }

      const isInAppliedCoupons = appliedCoupons.some(
        (c: any) => c?.id === offerId
      );
      // Manual-apply offers stay dormant until the user clicks Apply.
      if (!autoApply && !isInAppliedCoupons) return;
      // Hidden offers (isOfferVisible=false) are promo-code only — never auto-applied.
      if (offer?.isOfferVisible === false && !isInAppliedCoupons) return;

      // Exclusivity (combine_offer === true means the coupon can't stack with
      // any other):
      //   - any OTHER applied coupon is exclusive  → BOGO is blocked
      //   - this BOGO is exclusive AND any OTHER coupon is applied → blocked
      //   - this BOGO is exclusive AND any cart item already carries an
      //     item-level offer (top-level `offerId`, e.g. a menu "% Off") →
      //     blocked, since an exclusive offer can't coexist with the item offer.
      const thisIsExclusive = isExclusiveCoupon(offer);
      const otherExclusive = appliedCoupons.some(
        (c: any) => c?.id !== offerId && isExclusiveCoupon(c)
      );
      const anyOther = appliedCoupons.some((c: any) => c?.id !== offerId);
      // If this BOGO is already committed to appliedCoupons it wins — item-level
      // offers that arrive after it are item-reconcile's responsibility to revert.
      // Without `!isInAppliedCoupons`, an eager offerId stamp on a newly-added
      // cart item would cause BOGO to self-evict, then item-reconcile re-stamps,
      // then BOGO re-evicts → "Maximum update depth exceeded".
      const hasItemLevelOffer =
        !isInAppliedCoupons &&
        cartItems.some(
          (i: any) => i?.offerId && Number(i?.quantity || 0) > 0
        );
      const blockedByExclusivity =
        otherExclusive || (thisIsExclusive && (anyOther || hasItemLevelOffer));

      // Per-offer dismissal (set by the Coupons-page Remove flow). Releases
      // automatically when the local cart subtotal grows past the value at
      // dismissal. Scoped to this single offer id so removing one offer never
      // suppresses other offers — including others in the same category.
      const offerDismissed = isOfferDismissed(offerId, subTotalNow);

      // Existing discount target (if any).
      const appliedEntryIndex = cartItems.findIndex(
        (item: any) =>
          item?.offer_data?.offerId === offerId &&
          item?.is_discount_applied === true
      );
      const appliedEntry =
        appliedEntryIndex >= 0 ? cartItems[appliedEntryIndex] : null;

      // Eligibility — same-item BOGO shares the qty pool. Layered on top is
      // the equal-or-lesser-value gate: when the offer's bogo.equalOrLesserValue
      // flag is true, at least one in-cart get item must be priced ≤ the
      // highest-priced buy item, otherwise BOGO doesn't qualify regardless
      // of quantities. (Same-item BOGOs trivially pass — same price both sides.)
      const equalOrLesserOk = bogoEqualOrLesserSatisfied(
        bogo,
        cartItems,
        buyItemIds,
        getItemIds
      );
      const isSameItem =
        buyItemIds.length === 1 && getItemIds.includes(buyItemIds[0]);
      let isEligible: boolean;
      if (isSameItem) {
        const totalSame = cartItems.reduce(
          (sum: number, item: any) =>
            buyItemIds.includes(item?.itemId)
              ? sum + Number(item?.quantity || 0)
              : sum,
          0
        );
        isEligible = totalSame >= buyQty + 1;
      } else {
        let buyQtyInCart = 0;
        let getQtyInCart = 0;
        cartItems.forEach((item: any) => {
          const q = Number(item?.quantity || 0);
          if (q <= 0) return;
          if (buyItemIds.includes(item?.itemId)) {
            // Exclude the entry currently serving as the get target.
            const isThisOffersGet =
              item?.offer_data?.offerId === offerId &&
              item?.is_discount_applied === true;
            // Subtract qty already claimed by an earlier BOGO this pass —
            // a single meen kulambu can't unlock both "meen → idly" and
            // "meen → dosa" simultaneously.
            const alreadyClaimed = claimedBuyQty.get(item?.itemId) || 0;
            const available = Math.max(0, q - alreadyClaimed);
            buyQtyInCart += isThisOffersGet ? 0 : available;
          }
          if (getItemIds.includes(item?.itemId)) {
            getQtyInCart += q;
          }
        });
        isEligible = buyQtyInCart >= buyQty && getQtyInCart >= 1;
      }
      isEligible = isEligible && equalOrLesserOk;

      // Order-type gate: a BOGO restricted to other order types must not apply
      // (and must auto-remove) for the current type — so an auto_apply BOGO
      // doesn't re-apply after a delivery → pickup switch. Empty/missing
      // `orderTypeIds` = "all types".
      const orderTypeOk =
        !Array.isArray(offer?.orderTypeIds) ||
        offer.orderTypeIds.length === 0 ||
        offer.orderTypeIds.includes(restaurantOrderTypeId);

      const shouldBeApplied =
        isEligible &&
        orderTypeOk &&
        !blockedByExclusivity &&
        (isInAppliedCoupons ||
          (autoApply && !offerDismissed && !autoApplySuppressed));

      // Helper: per-unit price after applying the offer's discount type/value.
      // Operates on the BASE item price only — customization/add-on charges are
      // never discounted by a BOGO and are added back on top by the caller.
      const discountedUnitPriceFor = (baseUnitPrice: number): number => {
        const dt = String(bogo?.getDiscountType || "FREE").toUpperCase();
        const dv = Number(bogo?.getDiscountValue || 0);
        let unit: number;
        switch (dt) {
          case "FREE":
            unit = 0;
            break;
          case "PERCENT":
          case "PERCENTAGE":
            unit = baseUnitPrice * (1 - dv / 100);
            break;
          case "FLAT":
          case "FLATFEE":
            unit = Math.max(0, baseUnitPrice - dv);
            break;
          default:
            unit = 0;
        }
        return Math.max(0, unit);
      };

      // Helper: dispatch updateCartItem for a single cart item with the
      // computed claimedQty (units of this item that get the discount).
      const applyOfferToCartItem = (item: any, claimedQtyForThisItem: number) => {
        const unitPrice = computeEffectiveUnitPrice(item);
        // The BOGO discount applies to the base item price only. Customers still
        // pay for customization/add-ons on freed units, so split the modifier
        // charge out and add it back on top of the discounted base price.
        const baseUnit = Number(item?.price || 0);
        const modifiersUnit = unitPrice - baseUnit;
        const qty = Number(item?.quantity || 0);
        const discountedQty = Math.max(0, Math.min(qty, claimedQtyForThisItem));
        const undiscountedQty = qty - discountedQty;
        const discountedUnit = discountedUnitPriceFor(baseUnit) + modifiersUnit;
        const raw = undiscountedQty * unitPrice + discountedQty * discountedUnit;
        const newSubTotal = Math.round(raw * 100) / 100;
        const currentSubTotal = Number(item?.subTotal || 0);
        const alreadyMarked =
          item?.is_discount_applied === true &&
          item?.offer_data?.offerId === offerId;
        if (alreadyMarked && Math.abs(currentSubTotal - newSubTotal) <= 0.005) {
          return; // no-op
        }
        dispatch(
          updateCartItem(
            item.itemId,
            String(newSubTotal),
            true,
            qty,
            qty,
            {
              offerId,
              isBogoFreeItem: true,
              buyItemId: buyItemIds[0],
              buyQuantity: buyQty,
              getQuantity: getQty,
              getItemId: item.itemId,
              getItemIds,
              getDiscountType: bogo?.getDiscountType,
              getDiscountValue: bogo?.getDiscountValue,
            } as any,
            false,
            -1
          )
        );
      };

      // Helper: restore a cart item to its full undiscounted subTotal and
      // strip the offer_data marking. Used when a previously-claimed unit
      // is no longer claimed in this pass.
      const restoreCartItem = (item: any) => {
        const unitPrice = computeEffectiveUnitPrice(item);
        const qty = Number(item?.quantity || 0);
        dispatch(
          updateCartItem(
            item.itemId,
            String(unitPrice * qty),
            false,
            qty,
            qty,
            undefined,
            false,
            -1
          )
        );
      };

      if (shouldBeApplied) {
        // Shared BOGO engine — returns Map<itemId, claimedQty> and mutates
        // `claimedBuyQty` so later offers in this pass see fewer free buys.
        const itemClaims = computeBogoItemClaims(offer, cartItems, claimedBuyQty);

        // Apply: dispatch one updateCartItem per claimed cart entry.
        itemClaims.forEach((claimedQtyForItem, itemId) => {
          const item = cartItems.find((it: any) => it?.itemId === itemId);
          if (item) applyOfferToCartItem(item, claimedQtyForItem);
        });

        // Restore items previously marked with THIS offer but no longer in
        // the claims map (e.g. user removed a buy item so fewer gets are
        // claimable). Critical for the "previously-free reverts to paid"
        // half of the spec.
        cartItems.forEach((item: any) => {
          if (item?.offer_data?.offerId !== offerId) return;
          if (item?.is_discount_applied !== true) return;
          if (itemClaims.has(item?.itemId)) return;
          restoreCartItem(item);
        });

        // Coupons-list slot bookkeeping. Only register in `appliedCoupons`
        // if at least one item is actually being discounted; otherwise drop
        // a stale entry so the offers list stops misleading the user.
        if (!isInAppliedCoupons && itemClaims.size > 0) {
          dispatch(appliedCoupon(offer));
        } else if (isInAppliedCoupons && itemClaims.size === 0) {
          dispatch(clearAppliedCoupon({ id: offerId }));
          // const msg = `Offer '${c?.name ?? 'Offer'}' removed — order no longer qualifies`;
         const msg = `Some offers are no longer available. Your order has been updated.`;
          isMobile ? showErrorToast(msg, `offer-removed-${offerId}`, 3500) : showErrorToastDesktop(msg, `offer-removed-${offerId}`, 3500);
          openCartOnMenuPage();
        }
      } else if (isInAppliedCoupons || cartItems.some((it: any) =>
        it?.offer_data?.offerId === offerId && it?.is_discount_applied === true
      )) {
        // Was applied but no longer should be. Restore EVERY cart entry
        // currently marked with this offer (not just the first) — under
        // the multi-target engine a single offer can discount many items,
        // and they all need to flip back to full price together.
        cartItems.forEach((item: any) => {
          if (item?.offer_data?.offerId !== offerId) return;
          if (item?.is_discount_applied !== true) return;
          restoreCartItem(item);
        });
        if (isInAppliedCoupons) {
          dispatch(clearAppliedCoupon({ id: offerId }));
         // const msg = `Offer '${c?.name ?? 'Offer'}' removed — order no longer qualifies`;
         const msg = `Some offers are no longer available. Your order has been updated.`;
          isMobile ? showErrorToast(msg, `offer-removed-${offerId}`, 3500) : showErrorToastDesktop(msg, `offer-removed-${offerId}`, 3500);
          openCartOnMenuPage();
        }
      }
    });
  }, [cartItems, customerOffers, appliedCoupons, restaurantOrderTypeId, dispatch, tableId]);

  // --- Item-level offer reconciliation ---------------------------------------
  // Item-level offers (menu item carries an inline `offerId` + `offerPrice`)
  // are gated on customer login. When the user is anonymous we keep cart
  // items at regular price; once they log in we upgrade matching cart items
  // to the offer price + stamp the `offerId`. On logout we revert. The
  // watcher converges across page navigations without each add-to-cart site
  // having to consult the auth state.
  //
  // We also enqueue popup notifications for upgrades via a sessionStorage
  // hand-off that mobileCart's popup effect picks up and replays as one
  // celebration per upgraded item.
  // Subscribe to the redux auth slice so this service re-renders the
  // moment a login/logout dispatch fires — `getCookie` alone wouldn't
  // trigger a re-render since it's not React state. We watch BOTH `signedIn`
  // (set by the full email/password sign-in) AND `user` (set by the OTP-verify
  // and guest mobile-verify flows used on the cart page, which never flip
  // `signedIn`). Without the `user` subscription, logging in on the cart page
  // never re-ran this service and the offers fetch below silently never fired.
  const authSignedIn = useSelector((state: any) => state.auth?.signedIn);
  const authUser = useSelector((state: any) => state.auth?.user);
  // The USER_ID cookie is the real "we have a customerId to query offers for"
  // gate: it's written by EVERY login path (full sign-in, OTP verify, guest
  // mobile verify) and never exists for anonymous browsing. We gate on the
  // cookie, not on `signedIn`, because the cart-page flows leave `signedIn`
  // false. The auth selectors above exist only to make this recompute reactive.
  const customerLoggedIn = !!getCookie(USER_ID);

  useEffect(() => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) return;
    if (!Array.isArray(restaurantMenuDetails) || restaurantMenuDetails.length === 0) return;
    // CD (dine-in table) and Buzzer (always carries a tableId) orders don't
    // participate in offers — never auto-apply item-level discounts there.
    if (window.location.pathname.includes('/table/') || tableId) return;

    // Item-level offers and ORDER-level offers (including exclusive ones) always
    // coexist. No cross-category blocking is applied here.

    const upgradedItems: Array<{ itemId: string; itemName: string }> = [];

    cartItems.forEach((item: any) => {
      // Skip BOGO-claimed lines — the BOGO effect owns their price. Touching
      // them here would create an oscillation loop (item-reconcile reverts,
      // BOGO restores, repeat → "Maximum update depth exceeded").
      if (item?.is_discount_applied === true) return;

      const menuItem = restaurantMenuDetails.find(
        (m: any) => m?.itemId === item?.itemId
      );
      if (!menuItem) return;

      const regularPrice = Number(menuItem?.price);
      const offerPrice = Number(menuItem?.offerPrice);
      const menuHasOffer =
        menuItem?.offerId != null &&
        Number.isFinite(offerPrice) &&
        offerPrice >= 0 &&
        offerPrice < regularPrice;

      const cartPrice = Number(item?.price);
      const cartHasOfferApplied = item?.offerId != null;

      if (!menuHasOffer) {
        // Menu doesn't currently have an offer — if cart somehow stamped
        // one (stale), don't touch it here; that's a different reconcile.
        return;
      }

      // If the server rejected this offer (MAX_CAP_REACHED / OFFER_INACTIVE),
        // dismissOfferAtSubtotal marks it at MAX_SAFE_INTEGER — treat that as a
        // permanent session-level block. Revert the item to regular price so the
        // stamped offerId is cleared and the totals API isn't called again with it.
        const subTotalNow = computeRawCartSubtotal(cartItems);
        if (isOfferDismissed(menuItem.offerId, subTotalNow)) {
          const needsRevert =
            cartHasOfferApplied || Math.abs(cartPrice - regularPrice) > 0.005;
          if (needsRevert) {
            dispatch(
              reconcileItemOffer({
                itemId: item.itemId,
                modifierIds: item.modifierIds,
                apply: false,
                price: regularPrice,
                offerId: null,
                originalPrice: regularPrice,
              })
            );
          }
          return;
        }

        // maxDiscountCap clamps the PER-UNIT discount, then we multiply by
        // qty — so each unit can save at most `cap`, and total savings scale
        // with quantity (5 units × $5 cap → $25 off, not a flat $25 across
        // the line). The discount percentage is derived from the base price
        // pair (regularPrice → offerPrice) and applied to the full unit cost
        // (base + modifiers), so selected customizations are discounted too.
        const qty = Number(item?.quantity || 0);
        const modifiers = Array.isArray(item?.options)
          ? item.options.reduce(
              (sum: number, opt: any) =>
                sum +
                (Number(opt?.price) || 0) *
                  (Number(opt?.quantity) > 0 ? Number(opt?.quantity) : 1),
              0
            )
          : 0;
        const cap = Number(menuItem?.maxDiscountCap);
        const hasCap = Number.isFinite(cap) && cap > 0;
        const offerType = String(menuItem?.offerType ?? '').toUpperCase();
        const isFlat = offerType === 'FLAT' || offerType === 'FLATFEE';
        // FLAT/FLATFEE: use discountValue directly — offerPrice may be clamped to 0
        // when discountValue > regularPrice, so (regularPrice - offerPrice) understates the discount.
        // PERCENT/other: derive the rate from the base-price pair and apply it to the
        // full unit (base + modifiers) so customizations are discounted at the same rate.
        const rawPerUnitDiscount = isFlat
          ? Number(menuItem?.discountValue) || (regularPrice - offerPrice)
          : (regularPrice + modifiers) * (regularPrice > 0 ? (regularPrice - offerPrice) / regularPrice : 0);
        const unitCost = regularPrice + modifiers;
        // maxDiscountCap only applies to PERCENTAGE offers — for FLAT offers the
        // discount is already a fixed amount so capping it makes no sense.
        const cappedPerUnitDiscount = Math.min(
          hasCap && !isFlat ? Math.min(rawPerUnitDiscount, cap) : rawPerUnitDiscount,
          unitCost // discount can never exceed the full unit cost
        );
        const cappedDiscount = qty * cappedPerUnitDiscount;
        const expectedSubTotal =
          Math.max(0, Math.round((qty * unitCost - cappedDiscount) * 100) / 100);
        const currentSubTotal = Number(item?.subTotal || 0);
        // Effective per-unit price after the per-unit cap. When the raw
        // per-unit discount is within the cap this equals offerPrice; when it
        // exceeds, the user actually pays (regularPrice - cap) per unit, so
        // stamp that as the cart-line price.
        const effectiveUnitPrice =
          Math.max(0, Math.round((regularPrice - cappedPerUnitDiscount) * 100) / 100);

        // First-time application (offer not yet stamped, or price still at
        // regular) earns a celebration popup. A pure subtotal drift — same
        // offer already applied but the line total no longer matches the
        // capped value after a qty change — is corrected silently.
        const isFirstApply =
          !cartHasOfferApplied || Math.abs(cartPrice - effectiveUnitPrice) > 0.005;
        const subTotalDrift =
          Math.abs(currentSubTotal - expectedSubTotal) > 0.005;
        if (isFirstApply || subTotalDrift) {
          dispatch(
            reconcileItemOffer({
              itemId: item.itemId,
              modifierIds: item.modifierIds,
              apply: true,
              price: effectiveUnitPrice,
              offerId: menuItem.offerId,
              subTotal: expectedSubTotal,
              originalPrice: regularPrice,
            })
          );
          if (isFirstApply) {
            upgradedItems.push({
              itemId: item.itemId,
              itemName: menuItem.itemName || item.itemName || "",
            });
          }
        }
    });

    // Hand off upgraded items to mobileCart's popup queue. Sessionstorage
    // is read-once by the popup effect, which fires one celebration per
    // upgraded item. (Direct redux dispatch would couple this service to
    // page-specific UI state; the hand-off keeps the watcher self-contained.)
    if (upgradedItems.length > 0) {
      try {
        const existingRaw = sessionStorage.getItem("pendingItemOfferUpgrades");
        const existing = existingRaw ? JSON.parse(existingRaw) : [];
        const existingIds = new Set(
          Array.isArray(existing) ? existing.map((e: any) => e?.itemId) : []
        );
        const newItems = upgradedItems.filter((u) => !existingIds.has(u.itemId));
        const merged = Array.isArray(existing)
          ? [...existing, ...newItems]
          : newItems;
        sessionStorage.setItem(
          "pendingItemOfferUpgrades",
          JSON.stringify(merged)
        );
      } catch {
        /* sessionStorage unavailable — popup just won't fire this round */
      }
    }
  }, [cartItems, restaurantMenuDetails, customerLoggedIn, appliedCoupons, dispatch, tableId]);

  // --- Login: ensure offers are fetched -------------------------------------
  // Menu pages (RestaurantInfo on desktop, MenuPage on mobile) already
  // dispatch getCustomerOffersRequest when customerId becomes available, but
  // if the user logs in while ALREADY on the cart page (mobileCart) — or
  // anywhere else where neither of those is mounted — nothing fetches the
  // offers. The apply effects then have an empty list and silently skip.
  //
  // This global fetch closes that gap: whenever the user is logged in AND a
  // restaurant context is loaded AND we haven't successfully fetched offers
  // yet this session, kick off the request. Existing BOGO / item-offer /
  // ORDER auto-apply effects (here and in mobileCart) take it from there.
  const restaurantLocationId = useSelector(
    (state: any) => state.restaurant?.currentRestaurantDetail?.id
  );
  const customerOffersSuccess = useSelector(
    (state: any) => state.cart?.customerOffersSuccess
  );
  const customerOffersLoading = useSelector(
    (state: any) => state.cart?.customerOffersLoading
  );
  // Track whether the user was ever logged in this session. Used by the logout
  // cleanup below so that pure guest sessions (never logged in) are not treated
  // as a logout event and don't have their manually-applied coupons cleared.
  const wasLoggedInRef = useRef(false);
  useEffect(() => {
    if (customerLoggedIn) wasLoggedInRef.current = true;
  }, [customerLoggedIn]);

  // Track which customer the offers list was last fetched for, so a fresh
  // login (or a login-as-different-user) reliably re-fetches even if the
  // success flag persisted from a previous session.
  const lastFetchedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = getCookie(USER_ID) || '';
    if (!restaurantLocationId) return;
    if (customerOffersLoading) return;
    // Already fetched (or dispatched) for this uid — no re-fetch.
    if (lastFetchedForUserRef.current === uid) return;
    // OTP/profile flow already fetched for a logged-in uid — adopt without re-fetching.
    if (customerOffersSuccess && uid !== '') {
      lastFetchedForUserRef.current = uid;
      return;
    }
    lastFetchedForUserRef.current = uid;
    dispatch(
      getCustomerOffersRequest({
        locationId: String(restaurantLocationId),
      })
    );
  }, [
    customerLoggedIn,
    // `authSignedIn` / `authUser` re-run this effect on every login transition,
    // including the cart-page OTP / guest-verify flows that only set `user`.
    authSignedIn,
    authUser,
    restaurantLocationId,
    customerOffersSuccess,
    customerOffersLoading,
    dispatch,
  ]);

  // --- Logout cleanup --------------------------------------------------------
  // Coupons are personalised — when the user logs out, drop every applied
  // coupon (ORDER / FREE_ITEM / FREE_DELIVERY / BOGO) and clear any per-offer
  // dismissal entries so the next login starts from a clean slate. We also
  // clear the BOGO discounts on cart items (the cart-watcher's main BOGO
  // pass will then leave them at full price since `isInAppliedCoupons=false`
  // and `autoApply=true && offerDismissed=false` will only re-apply once the
  // user logs in again — but for anonymous users the offers list is
  // typically empty anyway since the offers API is gated on customerId).
  useEffect(() => {
    if (customerLoggedIn) return;
    // Skip cleanup for pure guest sessions (user was never logged in).
    // Only run when the user actually logged out mid-session so their
    // personalised coupons are cleared and guest users can apply offers freely.
    if (!wasLoggedInRef.current) return;
    // Reset the flag immediately so this block only runs ONCE per logout.
    // Without this, appliedCoupons.length changing (e.g. guest applies a coupon
    // after logout) would re-trigger this effect and wipe the newly applied offer.
    wasLoggedInRef.current = false;
    if (Array.isArray(appliedCoupons) && appliedCoupons.length > 0) {
      dispatch(clearAppliedCoupon());
    }
    // Wipe per-offer dismissal entries so prior session's "Remove" clicks
    // don't carry over to the next user.
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('autoApplyDismissedOffer:')) toRemove.push(k);
      }
      toRemove.forEach((k) => sessionStorage.removeItem(k));
      // Also clear the global auto-apply suppression so the next login starts
      // from a clean slate.
      clearAllAutoApplySuppression();
    } catch {
      /* sessionStorage unavailable */
    }
  }, [customerLoggedIn, appliedCoupons.length, dispatch]);

  return <Fragment></Fragment>;
};
